import React from 'react'
import {
  Autocomplete,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  SxProps,
  Theme,
} from '@mui/material'
import {CONTROL_HEIGHT, NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface Option {
  value: string
  label: string
}

interface SelectNewProps {
  value: string
  onChange: (value: string) => void
  options: Option[]
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: string
  fullWidth?: boolean
  showSearch?: boolean
  allowClear?: boolean
  sx?: SxProps<Theme>
  /**
   * Overrides the dropdown menu's own z-index (MUI's Select renders it via a Popover
   * portalled to document.body, at the theme's default modal z-index). Needed when this
   * field lives inside a container that already raised ITS OWN z-index above that default
   * (e.g. a dialog opened on top of another overlay) — without it the menu opens behind
   * that container instead of above it, and looks empty/unresponsive to the user.
   */
  menuZIndex?: number
}

const fieldsetSx = (error?: boolean) => ({
  '& .MuiOutlinedInput-root': {
    height: CONTROL_HEIGHT,
    borderRadius: '8px',
    backgroundColor: '#fff',
    '& fieldset': {borderColor: error ? 'error.main' : NEUTRAL_SCALE[200]},
    '&:hover fieldset': {borderColor: error ? 'error.main' : NEUTRAL_SCALE[300]},
    '&.Mui-focused fieldset': {borderColor: error ? 'error.main' : 'primary.main'},
  },
  '& .Mui-disabled': {opacity: 0.6},
})

export default function SelectNew({
  value,
  onChange,
  options,
  label,
  placeholder = 'Seleziona...',
  required,
  disabled,
  error,
  helperText,
  fullWidth,
  showSearch = false,
  allowClear = true,
  sx,
  menuZIndex,
}: SelectNewProps): JSX.Element {
  return (
    <Stack spacing={0.5} sx={{width: fullWidth ? '100%' : undefined, ...sx}}>
      {label && (
        <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
          {label}{required && ' *'}
        </Typography>
      )}

      {showSearch ? (
        <Autocomplete
          size="small"
          options={options}
          value={options.find(o => o.value === value) ?? null}
          onChange={(_event, opt) => onChange(opt?.value ?? '')}
          getOptionLabel={(o) => o.label}
          disabled={disabled}
          disableClearable={!allowClear}
          componentsProps={menuZIndex ? {popper: {sx: {zIndex: menuZIndex}}} : undefined}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              error={error}
              sx={fieldsetSx(error)}
            />
          )}
        />
      ) : (
        <Select
          size="small"
          value={value}
          onChange={(e: SelectChangeEvent) => onChange(e.target.value)}
          disabled={disabled}
          error={error}
          displayEmpty
          fullWidth={fullWidth}
          sx={fieldsetSx(error)}
          MenuProps={menuZIndex ? {sx: {zIndex: menuZIndex}} : undefined}
        >
          {allowClear && !required && (
            <MenuItem value="">
              <em>—</em>
            </MenuItem>
          )}
          {options.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      )}

      {helperText && (
        <Typography variant="caption" sx={{color: error ? 'error.main' : NEUTRAL_SCALE[500]}}>
          {helperText}
        </Typography>
      )}
    </Stack>
  )
}
